import PortalDialog, {PortalDialogProperties, PortalDialogState} from "../PortalDialog";
import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import PortalDialogType from "../../PortalDialogType";
import {Button} from "primereact/button";
import Parameter from "../../parameter/Parameter";
import PortalDocument from "../../../document/PortalDocument";
import MimeTypeFilter from "../../../document/mimetype/MimeTypeFilter";
import MimeTypes from "../../../document/mimetype/MimeTypes";
import {WebServiceType, WebServiceTypes} from "@softvision/webpdf-wsclient-9-typescript";
import DialogDefault from "../../parameter/PortalDialogDefault";
import PortalDocumentManager from "../../../document/PortalDocumentManager";
import MimeType from "../../../document/mimetype/MimeType";
import Message from "../../../../shared/log/Message";
import {MessageType} from "../../../../shared/log/MessageInterface";
import {
	AdminWebserviceStatus,
	BaseToolbox,
	Functions,
	Information,
	Settings,
	SettingsInterface,
	SignatureEditProfile,
	ToolboxSecuritySecurity
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import PortalDialogSecurity, {Intention} from "../../PortalDialogSecurity";
import Translate from "../../../../shared/translation/Translate";
import Helper from "../../../../shared/components/Helper";
import Icon from "../../../../shared/components/Icon";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";

export class DecryptDialogDefinition extends PortalDialog<ToolboxSecuritySecurity,
	PortalDialogProperties<ToolboxSecuritySecurity>,
	PortalDialogState<ToolboxSecuritySecurity>> {
	public static isEnabled(portalInfo: Information): boolean {
		return FunctionHelper.applyValue(
			portalInfo.webserviceStatus?.webservices?.toolbox?.enabled, AdminWebserviceStatus.getEnabledDefault()
		) && FunctionHelper.applyValue(
			portalInfo.functionsStatus?.encryptDocument, Functions.getEncryptDocumentDefault()
		);
	}

	public static isActive(
		selectedDocuments: Array<PortalDocument>, portalInfo: Information, signatureEditProfile: SignatureEditProfile
	): boolean {
		let mimeTypes: Array<MimeType> = [];

		for (let selectedDocument of selectedDocuments) {
			mimeTypes.push(selectedDocument.getMimeType());

			if (selectedDocument.isLocked()) {
				return false;
			}

			if (!PortalDialogSecurity.checkRestrictions(
				PortalDialogType.DECRYPT, signatureEditProfile, Intention.editingContent, selectedDocument
			)) {
				return false;
			}

			if (!selectedDocument.isEditable()) {
				return false;
			}

			if (!selectedDocument.hasPasswordProtection() && !selectedDocument.hasCertificateProtection()) {
				return false;
			}
		}

		return this.isEnabled(portalInfo) && selectedDocuments.length === 1 &&
			MimeTypeFilter.checkSelection(mimeTypes, [MimeTypes.PDF]);
	}

	public renderContents(): ReactNode {
		return (
			<>
				<fieldset className="p-col-12 p-grid">
					<div className="dialog-message">
						<Translate {...this.props} data="decrypt.message" namespace="dialog"/>
					</div>
				</fieldset>
			</>
		);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["decrypt-dialog"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide} resizable={false}
					closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()}
					appendTo={document.getElementById("root")}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.decrypt.general"} namespace={"help"} markdown={true}/>
					</Helper>
					<Helper {...this.props} type={MessageType.WARNING}>
						<Translate {...this.props} data={"dialog.decrypt.info"} namespace={"help"} markdown={true}/>
					</Helper>
					{this.renderContents()}
				</div>
			</Dialog>
		);
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-unlocked"}/>
					<Translate {...this.props} data="decrypt.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	protected async submit(): Promise<void> {
		await super.submit();

		let toolboxOperation: BaseToolbox = BaseToolbox.fromJson({
			security: this.state.parameter!.data.toJson()
		});

		let operationData: Array<BaseToolbox> = [toolboxOperation];
		let webServiceType: WebServiceType = WebServiceTypes.TOOLBOX;
		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let selection: Array<PortalDocument> = documentManager.getSelection();

		for (let portalDocument of selection) {
			try {
				await this.props.context.session.executeRequest({
					webServiceType: webServiceType,
					document: portalDocument,
					operation: operationData,
					settings: Settings.fromJson({
						pdfa: {
							pdfaEditProfile: this.props.pdfaEditProfile
						},
						signature: {
							signatureEditProfile: this.props.signatureEditProfile
						},
						compress: {
							compressProfile: this.props.compressProfile,
							compressObjects: {
								objectStreamSize: this.props.objectStreamSize
							}
						}
					} as SettingsInterface)
				});
			} catch (exception: any) {
				if (await this.onError(exception)) {
					await this.submit();
				}
				return;
			}

			let message: Message = new Message("success.toolbox.decrypt", MessageType.SUCCESS);
			message.context = {filename: '"' + portalDocument.getFileName() + '" '};
			this.props.context.getPage().addMessage(message);
			this.hide();
		}
	}

	protected getDialogType(): PortalDialogType {
		return PortalDialogType.DECRYPT;
	}

	protected getDefaultParameter(): Parameter<ToolboxSecuritySecurity> {
		return DialogDefault.getParameter(
			this.getDialogType(), this.props.context.documentManager.getSelection()
		);
	}
}

const DecryptDialog = React.memo(DecryptDialogDefinition,
	(
		prevProps: Readonly<PortalDialogProperties<ToolboxSecuritySecurity>>,
		nextProps: Readonly<PortalDialogProperties<ToolboxSecuritySecurity>>
	) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default DecryptDialog;